/**
 * Map a 0–100 “more / fewer trades” dial into concrete strategy knobs.
 * 0 = few / strict, 100 = many / loose. Shorts are biased stricter.
 */
export const clampDensity = (n, fallback = 50) => {
    const v = Number(n);
    if (!Number.isFinite(v))
        return fallback;
    return Math.max(0, Math.min(100, v));
};
/** Volume-churn / trend-avg density → lookback / pullback / hold. */
export const volumeChurnFromDensity = (side, density) => {
    const t = clampDensity(density, side === 'short' ? 28 : 55) / 100;
    if (side === 'short') {
        // Stricter shorts: longer lookback, deeper pullback, often breakout-only
        return {
            breakoutLookback: Math.round(26 - t * 14), // 26 → 12
            pullbackPct: Number((1.8 - t * 0.9).toFixed(2)), // 1.8 → 0.9
            holdBars: Math.round(10 + (1 - t) * 10), // 20 → 10
            entryMode: t < 0.4 ? 'breakout' : 'both',
            stepPercent: Number((0.85 - t * 0.25).toFixed(2)), // wider adds
        };
    }
    return {
        breakoutLookback: Math.round(16 - t * 12), // 16 → 4
        pullbackPct: Number((1.0 - t * 0.7).toFixed(2)), // 1.0 → 0.3
        holdBars: Math.round(8 + (1 - t) * 8), // 16 → 8
        entryMode: 'both',
        stepPercent: Number((0.55 - t * 0.2).toFixed(2)),
    };
};
/**
 * HiDeep density → RSI threshold + reference min distance from SMA.
 * 0 = few / deep dips; 100 = many / milder pullbacks.
 * `minDistPct` is for a ~1.5% ATR market — scale with `scaleHideepMinDist(atrPct)`
 * so quiet pairs (XAUT) and wild alts keep a useful dial range.
 * Fractal wings are a separate desk dial (0 = off, 1–5 = stricter confirm).
 */
export const hideepFromDensity = (side, density) => {
    const t = clampDensity(density, side === 'short' ? 25 : 50) / 100;
    if (side === 'short') {
        return {
            rsiShortAbove: Math.round(96 - t * 28), // 96 → 68
            minDistPct: Number((3.2 - t * 2.7).toFixed(2)), // 3.2 → 0.5 (ref @ ~1.5% ATR)
        };
    }
    return {
        rsiLongBelow: Math.round(8 + t * 36), // 8 → 44
        minDistPct: Number((2.2 - t * 1.9).toFixed(2)), // 2.2 → 0.3 (ref @ ~1.5% ATR)
    };
};
/** Reference ATR% that `hideepFromDensity().minDistPct` was tuned for. */
export const HIDEEP_REF_ATR_PCT = 1.5;
/**
 * Scale Snap/HiDeep SMA-gap by local volatility.
 * Quiet gold (~0.3–0.5% ATR) → lower bar; 3–5% alts → higher bar.
 * Clamped so the density dial still spans “few ↔ many” on every pair.
 */
export const scaleHideepMinDist = (refMinDistPct, atrPct) => {
    const ref = Number(refMinDistPct);
    if (!(ref > 0))
        return 0.12;
    const atr = Number(atrPct);
    const a = Number.isFinite(atr) && atr > 0 ? atr : HIDEEP_REF_ATR_PCT;
    const scaled = ref * (a / HIDEEP_REF_ATR_PCT);
    // Floor keeps ultra-quiet tapes from requiring ~0; cap stops one spike from locking the dial.
    const lo = Math.max(0.08, ref * 0.12);
    const hi = Math.max(lo + 0.05, ref * 1.65);
    return Number(Math.min(hi, Math.max(lo, scaled)).toFixed(3));
};
/** Wilder’s ATR as % of close at `index` (needs `length` prior bars). */
export const atrPctAt = (candles, index, length = 14) => {
    const n = Math.max(2, Math.floor(length) || 14);
    if (index < n || !candles.length)
        return null;
    let sum = 0;
    for (let i = index - n + 1; i <= index; i++) {
        const c = candles[i];
        const prev = candles[i - 1];
        if (!c || !prev)
            return null;
        const tr = Math.max(Number(c.high) - Number(c.low), Math.abs(Number(c.high) - Number(prev.close)), Math.abs(Number(c.low) - Number(prev.close)));
        if (!Number.isFinite(tr))
            return null;
        sum += tr;
    }
    const atr = sum / n;
    const px = Number(candles[index].close);
    if (!(px > 0) || !(atr > 0))
        return null;
    return (atr / px) * 100;
};
/** Clamp fractal wings: 0 = off, 1–5 = Williams wings (higher = stricter). */
export const clampFractalWings = (n, fallback = 2) => {
    const v = Number(n);
    if (!Number.isFinite(v))
        return fallback;
    return Math.max(0, Math.min(5, Math.round(v)));
};
/**
 * RedFlag / DCA density → grid step + per-leg TP.
 * More trades = tighter step (more levels hit); TP scales with the grid.
 * Shorts start a touch wider (stricter / fewer fills).
 */
export const redflagFromDensity = (side, density) => {
    const t = clampDensity(density, side === 'short' ? 28 : 55) / 100;
    if (side === 'short') {
        return {
            stepPercent: Number((0.7 - t * 0.45).toFixed(2)), // 0.70 → 0.25
            tpPercent: Number((0.18 + t * 0.12).toFixed(2)), // 0.18 → 0.30
        };
    }
    return {
        stepPercent: Number((0.55 - t * 0.45).toFixed(2)), // 0.55 → 0.10
        tpPercent: Number((0.12 + t * 0.18).toFixed(2)), // 0.12 → 0.30
    };
};
/** Book chip density → strength floor / status / OB sensitivity / hold / approach. */
export const bookFromDensity = (side, density) => {
    const t = clampDensity(density, side === 'short' ? 40 : 55) / 100;
    if (side === 'short') {
        return {
            minStrength: Math.round(58 - t * 28), // 58 → 30
            allowTested: t >= 0.35,
            sensitivity: Math.round(32 + t * 42), // 32 → 74
            holdBars: Math.round(12 + (1 - t) * 8), // 20 → 12
            maxBlocks: Math.round(4 + t * 3), // 4 → 7
            approachPct: Number((0.08 + t * 0.32).toFixed(2)), // 0.08 → 0.40
        };
    }
    return {
        minStrength: Math.round(52 - t * 28), // 52 → 24
        allowTested: t >= 0.25,
        sensitivity: Math.round(40 + t * 45), // 40 → 85
        holdBars: Math.round(8 + (1 - t) * 8), // 16 → 8
        maxBlocks: Math.round(5 + t * 3), // 5 → 8
        approachPct: Number((0.10 + t * 0.35).toFixed(2)), // 0.10 → 0.45
    };
};
